import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import type { Connector, ConnectorProduct, Probe } from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { ErrorBanner } from './common'
import { GuidaJira } from './GuidaJira'
import { SceltaProxy, proxyIniziale, proxyPayload } from './SceltaProxy'
import { t } from '../i18n/lingua'

/**
 * Il modulo di un connettore, in creazione e in modifica.
 *
 * Uno solo per entrambe, e non per risparmiare righe: due moduli separati
 * divergono alla prima modifica: si aggiunge un campo in creazione, ci si
 * dimentica della modifica, e il connettore diventa configurabile una volta
 * sola. In questa pagina è già successo con l'esecuzione da sonda, che senza
 * un modulo di modifica non si sarebbe potuta cambiare mai più.
 *
 * ## I campi segreti già configurati
 *
 * Una password non torna mai indietro dal Control Plane, e questo è giusto. Ma
 * un campo vuoto in modifica è ambiguo — il token non c'è, o c'è e non si vede?
 * — e l'ambiguità porta a due esiti entrambi cattivi: salvare il vuoto sopra il
 * token, oppure obbligare a ridigitarlo per cambiare il nome del connettore.
 *
 * Qui il campo già configurato si mostra **pieno di stelline e non
 * modificabile**, con accanto «Cambia». Finché nessuno preme quel pulsante il
 * segreto non viene nemmeno spedito: `secret` assente vuol dire «non toccarlo».
 */
export default function ConnectorForm({
  product,
  connector,
  onSaved,
}: {
  product: ConnectorProduct | undefined
  /** Assente in creazione. */
  connector?: Connector
  onSaved: () => void
}) {
  const probes = useAsync(() => api.probes(), [])

  const [name, setName] = useState(connector?.name ?? '')
  const [baseUrl, setBaseUrl] = useState(connector?.base_url ?? '')
  const [values, setValues] = useState<Record<string, string>>({})
  const [verifyTls, setVerifyTls] = useState(connector?.verify_tls ?? true)
  const [execution, setExecution] = useState(connector?.execution ?? 'cloud')
  const [applianceId, setApplianceId] = useState(connector?.appliance_id ?? '')
  const [proxy, setProxy] = useState(() => proxyIniziale(connector?.proxy))
  //: Quali segreti l'operatore ha deciso di riscrivere. Gli altri non partono.
  const [changing, setChanging] = useState<string[]>([])
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  // Cambiando prodotto i campi del precedente non esistono più: tenerli
  // significherebbe spedire il token di ServiceNow in un campo di Remedy.
  useEffect(() => {
    if (!connector) {
      setValues({})
      setChanging([])
    }
  }, [product?.kind, connector])

  const configured = new Set(connector?.secret_fields ?? [])
  const online = (probes.data ?? []).filter((p: Probe) => p.enabled)

  function isLocked(field: string): boolean {
    return configured.has(field) && !changing.includes(field)
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!product) return
    setError(null)
    setBusy(true)
    try {
      const config: Record<string, string> = {}
      const secret: Record<string, string> = {}
      let toccato = false
      for (const field of product.fields) {
        const value = (values[field.name] ?? '').trim()
        if (field.secret && isLocked(field.name)) continue
        // Un campo vuoto viene omesso: vuoto e non fornito sono due cose diverse.
        if (value === '') continue
        if (field.secret) {
          secret[field.name] = value
          toccato = true
        } else {
          config[field.name] = value
        }
      }
      const comune = {
        name: name.trim(),
        base_url: baseUrl.trim(),
        config,
        verify_tls: verifyTls,
        execution,
        appliance_id: execution === 'probe' ? applianceId : null,
        // Il proxy conta solo quando la chiamata parte da una sonda.
        ...(execution === 'probe' ? { proxy: proxyPayload(proxy) } : {}),
      }
      if (connector) {
        await api.updateConnector(connector.id, {
          ...comune,
          // `secret` assente lascia il segreto com'è. Mandare `{}` vorrebbe
          // dire «togli tutto», e chi correggeva un indirizzo cancellerebbe il
          // token senza averlo chiesto.
          ...(toccato ? { secret } : {}),
        })
      } else {
        await api.createConnector({ ...comune, kind: product.kind, secret })
        setName('')
        setBaseUrl('')
        setValues({})
      }
      setChanging([])
      onSaved()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="row">
        <label className="field">
          <span>{t('Nome')}</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('ITSM di produzione')}
            required
          />
          <span className="field__hint">
            {t('Nome con cui è identificato il collegamento, utile per distinguere ambienti diversi, ad esempio produzione e collaudo.')}
          </span>
        </label>
        <label className="field">
          <span>{t('Indirizzo')}</span>
          <input
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={product?.base_url_hint ?? 'https://…'}
            required
          />
        </label>
      </div>

      <div className="row">
        <label className="field">
          <span>{t('Da dove si parla')}</span>
          <select value={execution} onChange={(e) => setExecution(e.target.value)}>
            <option value="cloud">{t('Dal cloud')}</option>
            <option value="probe">{t('Da una sonda')}</option>
          </select>
          <span className="field__hint">
            {t("Per i sistemi raggiungibili solo dalla rete interna, le chiamate devono essere eseguite tramite una sonda con accesso alla rete di destinazione.")}
          </span>
        </label>
        {execution === 'probe' && (
          <label className="field">
            <span>{t('Sonda')}</span>
            <select
              value={applianceId ?? ''}
              onChange={(e) => setApplianceId(e.target.value)}
              required
            >
              <option value="">{t('— scegli —')}</option>
              {online.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.status === 'online' ? '' : ` (${p.status})`}
                </option>
              ))}
            </select>
            <span className="field__hint">
              {t('La credenziale del sistema di destinazione passerà da questa sonda: cifrata in transito, mai su disco, mai nei log.')}
            </span>
          </label>
        )}
      </div>
      {execution === 'probe' && (
        <SceltaProxy
          value={proxy}
          onChange={setProxy}
          salvata={connector?.proxy?.has_password}
          sonda={online.find((p) => p.id === applianceId)}
        />
      )}

      {/* Detta prima della configurazione, non dopo: scoprirlo alla prima prova
          significa rifare il giro con chi amministra la rete. */}
      {product?.reachability && execution === 'cloud' && (
        <p className="banner banner--info">{t(product.reachability)}</p>
      )}

      {product?.kind === 'jira' && <GuidaJira />}

      <div className="row">
        {(product?.fields ?? []).map((field) => (
          <label className="field" key={field.name}>
            <span>
              {t(field.label)}
              {field.secret ? ' 🔒' : ''}
            </span>
            <div className="field__with-action">
              <input
                type={field.secret ? 'password' : 'text'}
                value={isLocked(field.name) ? '••••••••••••' : (values[field.name] ?? '')}
                placeholder={field.placeholder ? t(field.placeholder) : field.placeholder}
                autoComplete="off"
                readOnly={isLocked(field.name)}
                onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
              />
              {field.secret && configured.has(field.name) && (
                <button
                  type="button"
                  className="secondary"
                  onClick={() =>
                    setChanging((corrente) =>
                      corrente.includes(field.name)
                        ? corrente.filter((n) => n !== field.name)
                        : [...corrente, field.name],
                    )
                  }
                >
                  {changing.includes(field.name) ? t('Annulla') : t('Cambia')}
                </button>
              )}
            </div>
            {isLocked(field.name) ? (
              <span className="field__hint">
                {t('Configurato. Non torna indietro dal Control Plane: per sostituirlo premi «Cambia», altrimenti resta quello.')}
              </span>
            ) : (
              field.help && <span className="field__hint">{t(field.help)}</span>
            )}
          </label>
        ))}
      </div>

      <label className="checkbox">
        <input
          type="checkbox"
          checked={verifyTls}
          onChange={(e) => setVerifyTls(e.target.checked)}
        />
        <span>
          {t('Verifica il certificato TLS del sistema. Disabilitala solo se il certificato interno non può essere validato tramite una CA attendibile.')}
        </span>
      </label>

      <ErrorBanner error={error ?? probes.error} />
      <button type="submit" disabled={busy || !product}>
        {busy ? t('Salvataggio…') : connector ? t('Salva modifiche') : t('Crea connettore')}
      </button>
    </form>
  )
}
